import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import { FormikProps } from 'formik';
import Button from '../../components/general/Button';
import HorizontalContainer from '../../components/structure/HorizontalContainer';
import ImageUploadContainer from '../../components/structure/ImageUploadContainer';
import AvatarFileUploadField from '../../components/forms/AvatarFileUploadField';
import FormFieldsContainer from '../../components/structure/FormFieldsContainer';
import TextField from '../../components/forms/TextField';
import TextAreaField from '../../components/forms/TextAreaField';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import ModalTitle from '../../components/general/ModalTitle';
import ApplicationEnvironmentsContainer, { ApplicationEnvironmentsOption } from '../../state/containers/ApplicationEnvironmentsContainer';
import ApplicationEnvironmentModel from '../../models/applications/api/environments/ApplicationEnvironmentModel';
import CheckboxField from '../../components/forms/CheckboxField';
import Switch from '../../components/forms/Switch';
import ContentCollapsingSection from '../../components/structure/ContentCollapsingSection';
import styled from '@emotion/styled';
import StyledDropdown from '../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { color_text_default, light_black } from '../../constants/colors';
import { ADORA, OLO, PAR, PAYTRONIX, SPEEDLINE, SQUARE, TOAST } from '../../constants/posNames';
import moment from "moment-timezone";
import * as applicationsApi from '../../api/controllers/applications';
import RemoveApplicationFeatureFlagModal from '../applicationSettings/components/RemoveApplicationFeatureFlagModal';
import { DeepAnalysisFeatureFlag, DeepAnalysisFeatureFlagId } from '../../constants/featureFlagIds';

const advancedSettingsIcon = require('../../content/images/application-settings/advanced-settings.svg');
const importIcon = require('../../content/images/refresh.svg');

export interface ApplicationInformationFormProps {
    appContainer: ApplicationContainer;
    app: ApplicationModel;
    organizationId?: string;
    languageIds?: string[];
    environmentOption?: ApplicationEnvironmentsOption;
    environment?: ApplicationEnvironmentModel;
    environmentsContainer: ApplicationEnvironmentsContainer;
    imageUrl?: string;
}

export interface ApplicationInformationFormData {
    name?: string
    invocationPhrase?: string
    shortDescription?: string
    description?: string
    keywords?: string
    timeZone?: string,
    posConfig?: string,
    imageFile: File
    imageItemId?: string
    featureFlags?: FeatureFlagModel[]
    selectedFlagIds?: string[],
    manageMenuApplicationId?: string,
    posName?: string,
    posLocationId?: string,
    outboundCallLocationId?: string,
    outboundCallIntegrationName?: string,
    isLive?: boolean,
}
const posNameOptions = [
    { label: "Adora", value: ADORA },
    { label: "Custom", value: "Custom" },
    { label: "Olo", value: OLO },
    { label: "Paytronix", value: PAYTRONIX },
    { label: "Speedline", value: SPEEDLINE },
    { label: "Square", value: SQUARE },
    { label: "Toast", value: TOAST },
    { label: "Par", value: PAR },
]

const OutboundCallIntegrationOptions = [
    { label: "Denticon", value: "Denticon" },
    { label: "Dentrix Ascend", value: "DentrixAscend" },
    { label: "OpenDental", value: "OpenDental" },
    { label: "Epic", value: "Epic" },
    { label: "Salesforce", value: "Salesforce" }
]
interface PosConfigSource {
    applicationId: string;
    applicationName: string;
}

const ApplicationInformationForm: React.FC<ApplicationInformationFormProps & FormikProps<ApplicationInformationFormData>> = (props) => {

    const [imageUrl, setImageUrl] = useState(props.app.imageUrl);
    const [isPosConfigLoading, setIsPosConfigLoading] = useState(false);
    const [isPosConfigSourcesLoading, setIsPosConfigSourcesLoading] = useState(false);
    const [isPosConfigCopyLoading, setIsPosConfigCopyLoading] = useState(false);
    const [posConfigSourceStatus, setPosConfigSourceStatus] = useState('');
    const [posConfigSources, setPosConfigSources] = useState<PosConfigSource[]>([]);
    const [showWorldPayCredentialsWarning, setShowWorldPayCredentialsWarning] = useState(false);
    const [featureFlagPendingRemoval, setFeatureFlagPendingRemoval] = useState<FeatureFlagModel | null>(null);
    const [featureFlagRemovalErrors, setFeatureFlagRemovalErrors] = useState<string[]>([]);

    useEffect(() => {
        props.appContainer.loadFeatureFlags();
        // set default pos name value if one isn't saved yet
        if (!props.app.posName)
            props.setFieldValue("posName", "SPEEDLINE");

        if (props.appContainer.state.posConfig) {
            props.setFieldValue("posConfig", props.appContainer.state.posConfig);
        }
    }, []);

    const isLoading = props.appContainer.state.isCreatingApplication;
    const isRemovingFeatureFlag = props.appContainer.state.isRemovingFeatureFlag;
    const allFeatureFlags = props.appContainer.state.featureFlags?.some(flag => flag.id === DeepAnalysisFeatureFlagId)
        ? props.appContainer.state.featureFlags
        : [...(props.appContainer.state.featureFlags ?? []), DeepAnalysisFeatureFlag];

    const loadPosConfigSources = async () => {
        const organizationId = props.organizationId || props.appContainer.state.organizationId;
        if (!organizationId) {
            setPosConfigSources([]);
            return;
        }

        setIsPosConfigSourcesLoading(true);
        setPosConfigSourceStatus('');
        try {
            const applicationsResult = await applicationsApi.getApplicationsForOrg(organizationId);
            if (applicationsResult.resultType !== 'Ok' || !applicationsResult.data) {
                setPosConfigSources([]);
                return;
            }

            const menuApplicationIds = new Set(
                applicationsResult.data.map(app => app.manageMenuApplicationId).filter(Boolean)
            );
            const sourceApplications = applicationsResult.data.filter(app => {
                const name = (app.name || '').toLowerCase();
                const isMenuApplication = menuApplicationIds.has(app.id) ||
                    name.includes('manage menu') ||
                    name.includes('manage entities') ||
                    (name.startsWith('x -') && name.endsWith('menu'));

                return !!app.id &&
                    app.id !== props.app.id &&
                    !app.allowsTemplating &&
                    !isMenuApplication;
            });

            const sources: PosConfigSource[] = sourceApplications.map(sourceApp => ({
                applicationId: sourceApp.id!,
                applicationName: sourceApp.name || sourceApp.id!
            }));
            setPosConfigSources(sources.sort((a, b) => a.applicationName.localeCompare(b.applicationName)));
        } catch {
            setPosConfigSources([]);
        } finally {
            setIsPosConfigSourcesLoading(false);
        }
    };

    const handleLoadPosConfig = async () => {
        if (!props.app.id) return;

        setIsPosConfigLoading(true);
        setShowWorldPayCredentialsWarning(false);
        const posConfigResult = await props.appContainer.loadPOSConfig(props.app.id);
        setShowWorldPayCredentialsWarning(
            posConfigResult?.posName?.toUpperCase() === SPEEDLINE &&
            posConfigResult?.hasWorldPayValues === false
        );
        props.setFieldValue("posConfig", props.appContainer.state.posConfig);
        const posConfig = props.appContainer.state.posConfig;
        if (!posConfig?.trim()) {
            await loadPosConfigSources();
        } else {
            setPosConfigSources([]);
        }
        setIsPosConfigLoading(false);
    };

    const handlePrepopulatePosConfig = () => {
        props.setFieldValue('posConfig', JSON.stringify({
            applicationId: props.app.id || '',
            applicationSecret: props.app.secret || '',
            'voicify_x_api-key': ''
        }, null, 4));
    };

    const handlePosConfigSourceChange = async (_: string, applicationId: string | boolean) => {
        const source = posConfigSources.find(posConfigSource => posConfigSource.applicationId === applicationId);
        if (!source) return;

        setIsPosConfigCopyLoading(true);
        setPosConfigSourceStatus(`Loading configuration from ${source.applicationName}...`);
        try {
            const configResult = await applicationsApi.getPOSConfig(source.applicationId);
            if (configResult.resultType !== 'Ok' || !configResult.data?.data?.trim()) {
                setPosConfigSourceStatus(`No POS configuration was found for ${source.applicationName}.`);
                return;
            }

            const sourceConfig = JSON.parse(configResult.data.data);
            if (!sourceConfig || typeof sourceConfig !== 'object' || Array.isArray(sourceConfig)) {
                setPosConfigSourceStatus(`The POS configuration for ${source.applicationName} is invalid.`);
                return;
            }

            const copiedConfig = {
                ...sourceConfig,
                applicationId: props.app.id || '',
                applicationSecret: props.app.secret || ''
            };
            const hasVoicifyApiKey = Object.prototype.hasOwnProperty.call(copiedConfig, 'voicify_x_api-key') ||
                Object.prototype.hasOwnProperty.call(copiedConfig, 'voicify_x_api_key');
            if (!hasVoicifyApiKey) {
                copiedConfig['voicify_x_api-key'] = '';
            }
            props.setFieldValue('posConfig', JSON.stringify(copiedConfig, null, 4));
            setPosConfigSourceStatus('');
        } catch {
            setPosConfigSourceStatus(`The POS configuration for ${source.applicationName} is invalid.`);
        } finally {
            setIsPosConfigCopyLoading(false);
        }
    };

    const handleFeatureFlagsChange = (flag: FeatureFlagModel) => {
        const flagsCopy = [...(props.values.selectedFlagIds ?? [])];
        if (flagsCopy.includes(flag.id)) {
            const isAssignedToApplication = props.app.featureFlags?.some(applicationFlag => applicationFlag.id === flag.id);
            if (isAssignedToApplication) {
                setFeatureFlagRemovalErrors([]);
                setFeatureFlagPendingRemoval(flag);
                return;
            }

            flagsCopy.splice(flagsCopy.indexOf(flag.id), 1);
        } else {
            flagsCopy.push(flag.id);
        }
        props.setFieldValue("selectedFlagIds", flagsCopy);
    };

    const closeFeatureFlagRemovalModal = () => {
        if (isRemovingFeatureFlag) return;

        setFeatureFlagPendingRemoval(null);
        setFeatureFlagRemovalErrors([]);
    };

    const confirmFeatureFlagRemoval = async () => {
        if (!featureFlagPendingRemoval?.id || !props.app.id) return;

        const result = await props.appContainer.removeApplicationFeatureFlag(props.app.id, featureFlagPendingRemoval.id);
        if (result.resultType !== "Ok") {
            setFeatureFlagRemovalErrors(result.errors?.length ? result.errors : ["The feature flag could not be removed."]);
            return;
        }

        props.setFieldValue(
            "selectedFlagIds",
            (props.values.selectedFlagIds ?? []).filter(featureFlagId => featureFlagId !== featureFlagPendingRemoval.id)
        );
        props.setFieldValue("featureFlags", result.data?.featureFlags ?? []);
        setFeatureFlagPendingRemoval(null);
        setFeatureFlagRemovalErrors([]);
    };

    const handlePOSNameChange = (optionValue: string, value: string | boolean) => {
        props.setFieldValue(optionValue, value);
    };
    const handleOutboundCallIntegrationNameChange = (optionValue: string, value: string | boolean) => {
        props.setFieldValue(optionValue, value);
    };
    const handleFoodOrderingGenerativeAIModelChange = (optionValue: string, value: string | boolean) => {
        const featureFlag = props.values.featureFlags?.find(f => f.name === optionValue);
        // featureFlag.GenerativeAIModel = value as string;
        props.setFieldValue("featureFlags", [...props.values.featureFlags]);
    };

    const usingCustomPOS = props.values.posName === "Custom" || !posNameOptions.some(o => o.value === props.values.posName);
    const renderFeatureFlags = () => {
        return allFeatureFlags.map((flag) => {
            return (
                <React.Fragment key={flag.id}>
                    <CheckboxField
                        name={flag.name}
                        label={flag.name}
                        labelStyle={checkboxLabelStyle}
                        disabled={isLoading || isRemovingFeatureFlag}
                        checked={(props.values.selectedFlagIds ?? []).includes(flag.id)}
                        onChange={() => handleFeatureFlagsChange(flag)}
                        className={checkboxField}
                    />
                    {
                        flag.name == "Food Ordering" &&
                        (props.values.selectedFlagIds ?? []).includes(flag.id) &&
                        <AdvancedSettingsFieldsWrapper>

                            <TextField
                                name="posLocationId"
                                disabled={isLoading}
                                value={props.values.posLocationId}
                                label="Location ID"
                                placeholder="The location ID in the POS"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <CustomDropdown
                                label="POS Name"
                                disabled={isLoading}
                                handleOnChange={handlePOSNameChange}
                                fieldName="posName"
                                options={posNameOptions}
                                size="medium"
                                searchable={true}
                                value={usingCustomPOS ? "Custom" : props.values.posName}
                            />
                            {
                                usingCustomPOS &&
                                <TextField
                                    name="posName"
                                    disabled={isLoading}
                                    value={props.values.posName}
                                    label="Custom POS Name"
                                    placeholder="The name of the POS"
                                    onChange={props.handleChange}
                                    onBlur={props.handleBlur}
                                />
                            }
                            <TextField
                                name="manageMenuApplicationId"
                                disabled={isLoading}
                                value={props.values.manageMenuApplicationId}
                                label="Manage Menu Application ID"
                                placeholder="The Id of the Voicify application that manages the menu"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                        </AdvancedSettingsFieldsWrapper>
                    }
                    {
                        flag.name == "Outbound Calling" &&
                        (props.values.selectedFlagIds ?? []).includes(flag.id) &&
                        <AdvancedSettingsFieldsWrapper>

                            <TextField
                                name="outboundCallLocationId"
                                disabled={isLoading}
                                value={props.values.outboundCallLocationId}
                                label="Location ID"
                                placeholder="The location ID or office ID"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <CustomDropdown
                                label="Integration"
                                disabled={isLoading}
                                handleOnChange={handleOutboundCallIntegrationNameChange}
                                fieldName="outboundCallIntegrationName"
                                options={OutboundCallIntegrationOptions}
                                size="medium"
                                searchable={true}
                                value={props.values.outboundCallIntegrationName}
                            />
                        </AdvancedSettingsFieldsWrapper>
                    }
                </React.Fragment>
            )
        });

    };

    const readDataUrl = () => {
        const reader = new FileReader();
        reader.onloadend = e => {
            setImageUrl(reader.result.toString())
        };
    };

    const handleImageSelected = (file: File) => {
        readDataUrl();
        props.setFieldValue("imageFile", file);
    };


    return (
        <>
            {featureFlagPendingRemoval &&
                <RemoveApplicationFeatureFlagModal
                    featureFlag={featureFlagPendingRemoval}
                    isLoading={isRemovingFeatureFlag}
                    errors={featureFlagRemovalErrors}
                    onClose={closeFeatureFlagRemovalModal}
                    onConfirm={confirmFeatureFlagRemoval}
                />
            }
            <form onSubmit={props.handleSubmit}>
                <ModalTitle>App Info</ModalTitle>
                <HorizontalContainer>
                    <FormFieldsContainer className={borderedFields}>
                        <NormalSettingsWrapper>
                            <p className="detail-label">
                                Here you can manage the public-facing aspects of your application.
                            </p>
                            <p className="detail-label">
                                Application Name is how you identify your app. Invocation Name is the phrase people will use to call up your application on their device
                            </p>
                            <TextField
                                name="name"
                                disabled={isLoading}
                                required
                                value={props.values.name}
                                label="Application Name:"
                                placeholder="Give your application a name"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <TextField
                                name="invocationPhrase"
                                disabled={isLoading}
                                tooltip="Invocation Name is the phrase people will use to call up your application on their device"
                                required
                                value={props.values.invocationPhrase}
                                label="Invocation Name:"
                                placeholder="The name used to start your app"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <TextField
                                name="shortDescription"
                                disabled={isLoading}
                                value={props.values.shortDescription}
                                label="Short Description:"
                                placeholder="Just something brief"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <TextAreaField
                                name="description"
                                disabled={isLoading}
                                value={props.values.description}
                                label="Detailed Description:"
                                placeholder="Now with more detail"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <TextField
                                name="keywords"
                                disabled={isLoading}
                                value={props.values.keywords}
                                label="Keywords:"
                                placeholder="These can help with finding your app/skill"
                                onChange={props.handleChange}
                                onBlur={props.handleBlur}
                            />
                            <CustomDropdown
                                label="Time Zone"
                                disabled={isLoading}
                                handleOnChange={props.setFieldValue}
                                fieldName="timeZone"
                                options={moment.tz.names().map(timezone => ({ value: timezone, label: timezone }))}
                                size="medium"
                                searchable={true}
                                value={props.values.timeZone}
                            />
                            <PosConfigContainer>
                                <label className="pos-config-label">
                                    POS Configuration:
                                </label>
                                <Button
                                    type="button"
                                    themes={["secondary-small"]}
                                    icon={importIcon}
                                    text={isPosConfigLoading ? "Loading..." : "Load POS Config"}
                                    disabled={isLoading || isPosConfigLoading}
                                    loading={isPosConfigLoading}
                                    onClick={handleLoadPosConfig}
                                    className="pos-config-button"
                                />
                                {props.values.posConfig != null && !props.values.posConfig.trim() && (
                                    <PosConfigSetup>
                                        <PosConfigWarning>
                                            <strong>Warning:</strong> This application does not have a POS configuration. Populate a starting config below, review it, and click Save to persist it.
                                        </PosConfigWarning>
                                        <PosConfigActions>
                                            <PosConfigAction>
                                                <PosConfigActionTitle>Option 1: Create a new configuration</PosConfigActionTitle>
                                                <PosConfigActionDescription>
                                                    Start with this location's applicationId and applicationSecret. The voicify_x_api-key will be left empty.
                                                </PosConfigActionDescription>
                                                <Button
                                                    type={'button'}
                                                    themes={['secondary-small']}
                                                    text={'Prepopulate new config'}
                                                    disabled={isLoading || isPosConfigCopyLoading}
                                                    onClick={handlePrepopulatePosConfig}
                                                    className={'pos-config-populate-button'}
                                                />
                                            </PosConfigAction>
                                            {(isPosConfigSourcesLoading || posConfigSources.length > 0 || posConfigSourceStatus) && (
                                                <>
                                                    <PosConfigActionDivider><span>OR</span></PosConfigActionDivider>
                                                    <PosConfigAction>
                                                        <PosConfigActionTitle>Option 2: Copy an existing configuration</PosConfigActionTitle>
                                                        <PosConfigActionDescription>
                                                            Choose another location to copy its POS settings into this location. applicationId and applicationSecret will be updated.
                                                        </PosConfigActionDescription>
                                                        {isPosConfigSourcesLoading ? (
                                                            <PosConfigSourcesStatus>Loading locations...</PosConfigSourcesStatus>
                                                        ) : posConfigSources.length > 0 && (
                                                            <PosConfigSourceDropdown
                                                                label="Source location"
                                                                placeholder="Select a location"
                                                                disabled={isLoading || isPosConfigCopyLoading}
                                                                handleOnChange={handlePosConfigSourceChange}
                                                                fieldName="posConfigSource"
                                                                options={posConfigSources.map(source => ({
                                                                    label: source.applicationName,
                                                                    value: source.applicationId
                                                                }))}
                                                                size="medium"
                                                                searchable={true}
                                                            />
                                                        )}
                                                        {posConfigSourceStatus && (
                                                            <PosConfigSourcesStatus>{posConfigSourceStatus}</PosConfigSourcesStatus>
                                                        )}
                                                    </PosConfigAction>
                                                </>
                                            )}
                                        </PosConfigActions>
                                    </PosConfigSetup>
                                )}
                                {showWorldPayCredentialsWarning && (
                                    <PosConfigWarning>
                                        <strong>Warning:</strong> This Speedline POS configuration does not have Worldpay credentials.
                                    </PosConfigWarning>
                                )}
                                {props.values.posConfig != null && (
                                    <>
                                        <TextAreaField
                                            name="posConfig"
                                            disabled={isLoading}
                                            value={props.values.posConfig}
                                            label=""
                                            placeholder="Enter POS configuration (JSON format)"
                                            className="field_value"
                                            onChange={props.handleChange}
                                        />
                                    </>
                                )}
                            </PosConfigContainer>
                        </NormalSettingsWrapper>

                        <ContentCollapsingSection
                            title="Advanced Options"
                            icon={advancedSettingsIcon}
                            isCollapsedByDefault={true}
                            className={collapsedSection}
                            bottom
                        >
                            {renderFeatureFlags()}
                            <Switch
                                checked={props.values.isLive ?? false}
                                onChange={(enabled) => props.setFieldValue('isLive', enabled)}
                                label="Go Live"
                            />
                        </ContentCollapsingSection>
                        <div className={saveButtonWrapperStyle}>
                            <Button
                                loading={isLoading}
                                disabled={isLoading}
                                themes={["primary"]}
                                text="Save"
                                type="submit"
                            />
                            {props?.status?.message && (
                                <StatusMessage>{props.status.message}</StatusMessage>
                            )}
                        </div>
                    </FormFieldsContainer>
                    <ImageUploadContainer>
                        <label>App Avatar</label>
                        <AvatarFileUploadField
                            imageUrl={imageUrl}
                            accept=".png"
                            onChange={handleImageSelected.bind(this)}
                        />
                    </ImageUploadContainer>
                </HorizontalContainer>
            </form>
        </>
    )
}

const CustomDropdown = styled(StyledDropdown)`
    margin: 8px 0 26px 0;
`;

const PosConfigContainer = styled.div`
    .pos-config-label {
        margin-bottom: 12px;
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: ${color_text_default};
    }

    .pos-config-button {
        margin: 0 0 16px 0 !important;

        .left-img {
            width: 16px;
            height: 16px;
            margin-right: 8px;
        }

        &:hover {
            transform: translateY(-1px);
            transition: transform 0.2s ease;
        }

        &:active {
            transform: translateY(0);
        }
    }
`;

const PosConfigSetup = styled.div`
    margin: 0 0 16px 0;
`;

const PosConfigWarning = styled.div`
    padding: 10px 12px;
    border: 1px solid #f6ad55;
    border-radius: 4px;
    background-color: #fffaf0;
    color: #744210;
    font-size: 13px;
    line-height: 1.5;
`;

const PosConfigActions = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: stretch;
    gap: 16px;
    margin-top: 12px;

    .pos-config-populate-button {
        margin: 0 !important;
        align-self: flex-start;
    }

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const PosConfigAction = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
    border: 1px solid #c7cfd2;
    border-radius: 6px;
    background-color: #fff;
`;

const PosConfigActionTitle = styled.div`
    margin-bottom: 6px;
    color: ${color_text_default};
    font-size: 14px;
    font-weight: 600;
`;

const PosConfigActionDescription = styled.div`
    min-height: 40px;
    margin-bottom: 12px;
    color: #666;
    font-size: 12px;
    line-height: 1.4;
`;

const PosConfigActionDivider = styled.div`
    display: flex;
    align-items: center;
    color: #8e97a1;
    font-size: 11px;
    font-weight: 600;

    @media (max-width: 1000px) {
        justify-content: center;
    }
`;

const PosConfigSourceDropdown = styled(StyledDropdown)`
    width: 100%;
`;

const PosConfigSourcesStatus = styled.div`
    margin-top: 12px;
    color: #666;
    font-size: 13px;
`;

const StatusMessage = styled.div`
    width: 30%;
    margin: auto;
    margin-top: 20px;
    padding: 10px;
    border-radius: 4px;
    background-color: ${light_black};
    color: ${color_text_default};
    font-size: 14px;
    text-align: center;
`;

const checkboxField = css`
    padding: 0 24px;
`;

const checkboxLabelStyle = css`
    font-size: 16px;
`;

const NormalSettingsWrapper = styled.div`
    padding-right: 64px;

    .field_value {
        width: 100%;
        margin-top: 14px;
        label {
            margin-left: 6px;
        }
        textarea {
            overflow-y: auto;
        }

        /* Special styling for POS config field */
        &[name="posConfig"] textarea {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            min-height: 150px;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            line-height: 1.4;
        }
    }
`;

const AdvancedSettingsFieldsWrapper = styled.div`
    padding-right: 34px;
    margin: auto;
    margin-left: 60px;
`;

const collapsedSection = css`
    margin-left: -50px;
    *.card-title-container {
        padding: 24px 24px 24px 40px;
    }
`;

const borderedFields = css`
    border-right: 1px solid #c7cfd2;
    margin-top: 0;

    .detail-label {
        font-family: Muli;
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.5px;
        color: #8e97a1;
        margin-bottom: 16px;
    }
`;

const saveButtonWrapperStyle = css`
    display: flex;
`;
export default ApplicationInformationForm
